import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, catchError, switchMap, tap } from 'rxjs/operators';
import { ShopService } from '@app/services/shops/shop.service';
import { UtilService } from '@app/core/services/utils/util.service';
import { CROSS_SITE_PARAMS, REGULAR_TEST_RESULTS_PARAMS } from '../constants';
import { DropdownOption as PdqDropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import { ImpactMetricsService } from '@app/services/impact-metrics/impact-metrics.service';
import { FeatureAccessService } from '@app/services/feature-access/feature-access.service';
import { UserService } from '@app/services/user/user.service';
import { FeatureKey } from '@pdqdev/pdq-shared/dist/utils/features-manifest';
import { AllTestsService } from './services/all-tests.service';
import { MaxxTestAnalysisService } from './services/maxx-test-analysis.service';
import { ALL_TIME_PRESET, DateRangeSelection } from '@app/storybook/components/datepicker/date-range-presets';
import {
  MaxxAnalysisError,
  MaxxAnalysisResponse,
  MaxxAnalysisRowState,
  MaxxAnalysisStatusItem,
  createIdleRowState,
} from './services/maxx-test-analysis.model';
import { OverviewMetrics, DEFAULT_OVERVIEW_METRICS } from '@app/pages/impact-dashboard/impact-dashboard-metric.models';
import {
  TestListItem,
  TestFilters,
  PaginationInfo,
  DEFAULT_PAGE_SIZE,
  STATUS_OPTIONS,
  TEST_TYPE_OPTIONS,
  SortState,
  SortableColumn,
  TableColumnOption,
  COLUMN_OPTIONS,
  LOCAL_STORAGE_COLUMNS_KEY,
  formatDateRange,
  TestType,
} from './all-tests-dashboard.models';

@Component({
  selector: 'all-tests-dashboard',
  templateUrl: './all-tests-dashboard.component.html',
  styleUrls: ['./all-tests-dashboard.component.scss'],
  standalone: false,
})
export class AllTestsDashboardComponent implements OnInit, OnDestroy {
  @Input() parameters: Record<string, string> = {};
  @Output() navigateToTestResults = new EventEmitter<{
    testName: string;
    isCrossSite: boolean;
    segmentWinnerClean: string | null;
  }>();

  get currencySymbol(): string {
    return this.shopService.shopCurrencySymbol || '$';
  }

  // Loading / error states
  loadingSummary = true;
  loadingTests = true;
  errorSummary = false;
  hasAnnualizedRevenue = false;
  hideRoi = false;

  // Data
  overviewMetrics: OverviewMetrics = { ...DEFAULT_OVERVIEW_METRICS };
  private runningTestsCount = 0;
  tests: TestListItem[] = [];

  // Pagination
  pagination: PaginationInfo = {
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    totalItems: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  };

  readonly defaultRange = ALL_TIME_PRESET;
  readonly shopCreated = inject(ShopService).shopCreatedSignal;

  // Filter options

  statusDropdownOptions: PdqDropdownOption[] = STATUS_OPTIONS.map((opt) => ({
    label: opt.label,
    value: opt.value,
  }));
  testTypeDropdownOptions: PdqDropdownOption[] = TEST_TYPE_OPTIONS.map((opt) => ({
    label: opt.label,
    value: opt.value,
  }));
  selectedStatusOption: PdqDropdownOption = this.statusDropdownOptions[0];
  selectedTestTypeOption: PdqDropdownOption = this.testTypeDropdownOptions[0];

  allPages: object = {};
  activePage = 1;

  sortState: SortState = { column: null, direction: null };

  COLUMN_OPTIONS: TableColumnOption[] = COLUMN_OPTIONS;
  selectedColumns: string[] = [];

  visibleColumnCount = 0;

  selectedStatus = 'all';
  selectedTestType = 'all';
  searchQuery = '';
  currentFilters: TestFilters = {};

  maxxAvailable = false;

  private readonly destroy$ = new Subject<void>();
  private readonly searchSubject$ = new Subject<string>();
  private readonly analysisReset$ = new Subject<void>();

  constructor(
    private allTestsService: AllTestsService,
    private impactMetricsService: ImpactMetricsService,
    private router: Router,
    private shopService: ShopService,
    private utilService: UtilService,
    private maxxAnalysisService: MaxxTestAnalysisService,
    private featureAccessService: FeatureAccessService,
    private userService: UserService,
  ) {}

  ngOnInit(): void {
    this.maxxAvailable = this.featureAccessService.isEnabled(FeatureKey.MAXX) && this.userService.isWrite();
    this.loadSelectedColumns();
    this.initializeFiltersFromParameters();
    this.setupSearchDebounce();
    this.loadSummaryMetrics();
  }

  ngOnDestroy(): void {
    this.analysisReset$.next();
    this.analysisReset$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onRefreshSummary(): void {
    this.loadSummaryMetrics();
  }

  onDateRangeChange(event: DateRangeSelection): void {
    // all time sends no dates, like before, so the server applies its own START_DATE <= today limit
    if (event.rangePreset === ALL_TIME_PRESET.text) {
      delete this.currentFilters.startDate;
      delete this.currentFilters.endDate;
    } else {
      this.currentFilters.startDate = this.impactMetricsService.formatDateForApi(new Date(event.start));
      this.currentFilters.endDate = this.impactMetricsService.formatDateForApi(new Date(event.end));
    }
    this.currentFilters.page = 0;
    this.activePage = 1;
    this.loadTestList();
  }

  onFilterChange(): void {
    this.currentFilters.status = this.selectedStatus;
    this.currentFilters.testType = this.selectedTestType;
    this.currentFilters.page = 0;
    this.activePage = 1;
    this.loadTestList();
  }

  onTestTypeChange(option: PdqDropdownOption): void {
    this.selectedTestType = option.value as string;
    this.selectedTestTypeOption = option;
    this.onFilterChange();
  }

  onStatusChange(option: PdqDropdownOption): void {
    this.selectedStatus = option.value as string;
    this.selectedStatusOption = option;
    this.onFilterChange();
  }

  onSearchInput(query: string): void {
    this.searchSubject$.next(query);
  }

  onTestClick(test: TestListItem): void {
    this.navigateToTestResults.emit({
      testName: test.testName,
      isCrossSite: test.type === TestType.CROSS_SITE,
      segmentWinnerClean: test.segmentWinnerClean ?? null,
    });
  }

  onStrategyClick(test: TestListItem, event: Event): void {
    event.stopPropagation();
    if (test.strategyCatalogId) {
      this.router.navigate(['/optimize/strategy-catalog', test.strategyCatalogId]);
    }
  }

  onSort(column: SortableColumn): void {
    if (this.sortState.column === column) {
      this.sortState.direction =
        this.sortState.direction === 'asc' ? 'desc' : this.sortState.direction === 'desc' ? null : 'asc';
      if (this.sortState.direction === null) {
        this.sortState.column = null;
      }
    } else {
      this.sortState = { column, direction: 'asc' };
    }

    this.currentFilters.sortColumn = this.sortState.column;
    this.currentFilters.sortDirection = this.sortState.direction;
    this.currentFilters.page = 0;
    this.activePage = 1;
    this.loadTestList();
  }

  onPageChange(page: number): void {
    this.activePage = page + 1;
    this.currentFilters.page = page;
    this.loadTestList();
  }

  getSortIcon(column: string): string {
    if (this.sortState.column !== column) return '\u25B2\u25BC';
    return this.sortState.direction === 'asc' ? '\u25B2' : '\u25BC';
  }

  isColumnVisible(prop: string): boolean {
    return this.selectedColumns.includes(prop);
  }

  toggleColumn(): void {
    localStorage.setItem(LOCAL_STORAGE_COLUMNS_KEY, JSON.stringify(this.selectedColumns));
    this.recomputeVisibleColumnCount();
  }

  onMaxxTriggerClick(test: TestListItem, event: Event): void {
    event.stopPropagation();

    const state = test.maxxAnalysis;
    if (!state || state.status === 'loading') {
      return;
    }

    if (state.status === 'idle') {
      this.patchMaxxState(test, { expanded: true });
      this.runAnalysis(test);
      return;
    }

    if (state.status === 'error') {
      this.patchMaxxState(test, { expanded: !state.expanded });
      return;
    }

    if (!state.expanded) {
      this.patchMaxxState(test, { expanded: true });
      if (!state.inFlight) {
        this.runAnalysis(test);
      }
      return;
    }

    this.patchMaxxState(test, { expanded: false });
  }

  onMaxxCollapse(test: TestListItem): void {
    this.patchMaxxState(test, { expanded: false });
    document.getElementById(`maxx-trigger-${test.testId}`)?.focus();
  }

  onMaxxRetry(test: TestListItem): void {
    this.runAnalysis(test);
  }

  onMaxxContinueInChat(threadId: number): void {
    this.router.navigate(['/optimize/maxx'], {
      queryParams: { ref: 'all_tests_dashboard' },
      fragment: `/chat/${threadId}`,
    });
  }

  private patchMaxxState(test: TestListItem, patch: Partial<MaxxAnalysisRowState>): void {
    if (!test.maxxAnalysis) {
      return;
    }
    test.maxxAnalysis = { ...test.maxxAnalysis, ...patch };
  }

  private runAnalysis(test: TestListItem): void {
    const state = test.maxxAnalysis;
    if (!state) {
      return;
    }

    this.patchMaxxState(test, {
      status: state.answer ? state.status : 'loading',
      inFlight: true,
      errorRetryable: false,
      errorMessage: null,
    });

    this.maxxAnalysisService
      .analyze(test.testId, this.buildAnalysisParameters(test))
      .pipe(takeUntil(this.analysisReset$), takeUntil(this.destroy$))
      .subscribe({
        next: (response) => this.onAnalysisResponse(test, response),
        error: (error: MaxxAnalysisError) => this.onAnalysisError(test, error),
      });
  }

  private onAnalysisResponse(test: TestListItem, response: MaxxAnalysisResponse): void {
    if (!test.maxxAnalysis) {
      return;
    }

    const patch: Partial<MaxxAnalysisRowState> = { threadId: response.threadId };

    if (response.answer) {
      patch.answer = response.answer;
      patch.status = 'loaded';
    }

    if (response.status === 'running' || response.isRegenerating) {
      if (!response.answer) {
        patch.status = 'loading';
      }
      this.patchMaxxState(test, { ...patch, inFlight: true });
      this.pollAnalysis(test);
      return;
    }

    if (response.status === 'failed') {
      patch.status = 'error';
      patch.errorRetryable = response.error?.retryable ?? false;
      patch.errorMessage = response.error?.message ?? null;
    }

    this.patchMaxxState(test, { ...patch, inFlight: false });
  }

  private pollAnalysis(test: TestListItem): void {
    this.maxxAnalysisService
      .pollUntilSettled(test.testId)
      .pipe(takeUntil(this.analysisReset$), takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.status !== 'running') {
            this.onAnalysisResponse(test, { ...response, isRegenerating: false });
          }
        },
        error: (error: MaxxAnalysisError) => this.onAnalysisError(test, error),
      });
  }

  private onAnalysisError(test: TestListItem, error: MaxxAnalysisError): void {
    this.patchMaxxState(test, {
      status: 'error',
      inFlight: false,
      errorRetryable: error?.retryable ?? true,
      errorMessage: null,
    });
  }

  private loadSelectedColumns(): void {
    const knownProps = this.COLUMN_OPTIONS.map((col) => col.prop);

    let stored: unknown = null;
    try {
      stored = JSON.parse(localStorage.getItem(LOCAL_STORAGE_COLUMNS_KEY) ?? 'null');
    } catch {
      stored = null;
    }

    const sanitized = Array.isArray(stored) ? (stored as string[]).filter((prop) => knownProps.includes(prop)) : [];
    this.selectedColumns = sanitized.length ? sanitized : knownProps;

    if (!Array.isArray(stored) || sanitized.length !== (stored as string[]).length) {
      localStorage.setItem(LOCAL_STORAGE_COLUMNS_KEY, JSON.stringify(this.selectedColumns));
    }

    this.recomputeVisibleColumnCount();
  }

  private recomputeVisibleColumnCount(): void {
    const count = this.COLUMN_OPTIONS.filter((col) => this.selectedColumns.includes(col.prop)).length;
    this.visibleColumnCount = Math.max(1, count);
  }

  private initializeFiltersFromParameters(): void {
    if (this.parameters.ShowCheckoutORCrossSiteAB) {
      const param = this.parameters.ShowCheckoutORCrossSiteAB;
      this.selectedTestType = param.toLowerCase().includes('cross') ? 'cross-site' : 'checkout';
      this.currentFilters.testType = this.selectedTestType;
    }
  }

  private setupSearchDebounce(): void {
    this.searchSubject$.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((query) => {
      this.searchQuery = query;
      this.currentFilters.searchQuery = query;
      this.currentFilters.page = 0;
      this.activePage = 1;
      this.loadTestList();
    });
  }

  private loadSummaryMetrics(): void {
    this.loadingSummary = true;
    this.errorSummary = false;

    this.impactMetricsService
      .loadOverviewMetricsWithBillingCheck(this.currencySymbol)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ hideRoi, metrics }) => {
          this.hideRoi = hideRoi;

          if (metrics) {
            this.hasAnnualizedRevenue = metrics.annualizedRevenueUplift !== `${this.currencySymbol}0`;
            this.overviewMetrics = { ...metrics, runningTests: this.runningTestsCount };
          } else {
            this.hasAnnualizedRevenue = false;
            this.errorSummary = true;
          }
          this.loadingSummary = false;
        },
        error: (err) => {
          console.error('Failed to load summary metrics', err);
          this.hasAnnualizedRevenue = false;
          this.hideRoi = false;
          this.errorSummary = true;
          this.loadingSummary = false;
        },
      });
  }

  private loadTestList(): void {
    this.loadingTests = true;
    this.analysisReset$.next();

    this.allTestsService
      .getTestList(this.currentFilters)
      .pipe(
        tap((response) => {
          this.tests = response.tests
            .map((test) => ({
              ...test,
              formattedDateRange: formatDateRange(test.startDate, test.endDate, test.plannedDays, test.status),
              isRunning: test.status === 'running',
              outliersDisplay: test.outliersImplementation?.toLowerCase().includes('incl')
                ? 'Incl Outliers'
                : 'Excl Outliers',
              maxxAnalysis: createIdleRowState(),
            }))
            .sort((a, b) => Number(b.isRunning) - Number(a.isRunning));
          this.pagination = response.pagination;
          this.runningTestsCount = response.runningTestsCount ?? 0;
          this.overviewMetrics = { ...this.overviewMetrics, runningTests: this.runningTestsCount };
          this.activePage = this.pagination.page + 1;
          this.utilService.setPages(this.activePage, this.allPages, this.pagination.totalPages);
          this.loadingTests = false;
        }),
        switchMap((response) => {
          if (!this.maxxAvailable) {
            return of([] as MaxxAnalysisStatusItem[]);
          }
          return this.maxxAnalysisService
            .getStatuses(response.tests.map((test) => test.testId))
            .pipe(catchError(() => of([] as MaxxAnalysisStatusItem[])));
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (items) => this.applyAnalysisStatuses(items),
        error: (err) => {
          console.error('Failed to load test list', err);
          this.loadingTests = false;
        },
      });
  }

  private buildAnalysisParameters(test: TestListItem): Record<string, string> {
    const allowed = [...REGULAR_TEST_RESULTS_PARAMS, ...CROSS_SITE_PARAMS] as readonly string[];
    const parameters: Record<string, string> = { testName: test.testName };

    for (const key of allowed) {
      const value = this.parameters[key];
      if (value) {
        parameters[key] = value;
      }
    }

    return parameters;
  }

  private applyAnalysisStatuses(items: MaxxAnalysisStatusItem[]): void {
    if (!items.length) {
      return;
    }

    const byTestId = new Map(items.map((item) => [item.testId, item]));

    for (const test of this.tests) {
      const item = byTestId.get(test.testId);
      if (!item || !test.maxxAnalysis) {
        continue;
      }

      const patch: Partial<MaxxAnalysisRowState> = { threadId: item.threadId };

      if (item.answer) {
        patch.answer = item.answer;
      }

      if (item.state === 'running') {
        this.patchMaxxState(test, { ...patch, status: item.answer ? 'loaded' : 'loading', inFlight: true });
        this.pollAnalysis(test);
        continue;
      }

      if (item.state === 'filled' && item.answer) {
        patch.status = 'loaded';
      } else if (item.state === 'failed') {
        patch.status = 'error';
        patch.errorRetryable = true;
      }

      this.patchMaxxState(test, patch);
    }
  }
}
